// src/admin/pages/EditCategoryPage.tsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaSave, FaTimesCircle, FaPlusCircle, FaFolderOpen, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import * as FaIcons from 'react-icons/fa';

interface Category {
  id: number;
  name: string;
  icon?: string;
  parentId?: number | null;
  subCategories?: Category[];
}

const EditCategoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState<string>('');
  const [parentCategory, setParentCategory] = useState<number | null>(null);
  const [existingCategories, setExistingCategories] = useState<Category[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const fetchExistingCategories = async () => {
    try {
      const response = await fetch('https://ayushvatika.com/api/categories', {
      // const response = await fetch('http://localhost:8484/api/categories', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data: Category[] = await response.json();
        const categoriesMap = new Map<number, Category>();
        data.forEach(cat => { categoriesMap.set(cat.id, { ...cat, subCategories: [] }); });
        
        const rootCategories: Category[] = [];
        data.forEach(cat => {
          if (cat.parentId === null || cat.parentId === undefined) {
            rootCategories.push(categoriesMap.get(cat.id)!);
          } else {
            const parent = categoriesMap.get(cat.parentId);
            if (parent) { parent.subCategories?.push(categoriesMap.get(cat.id)!); }
          }
        });
        rootCategories.sort((a, b) => a.name.localeCompare(b.name));
        setExistingCategories(rootCategories);
      }
    } catch (error) {
      console.error("Error fetching existing categories: ", error);
    }
  };

  const fetchCategoryForEdit = async () => {
    if (!id) {
        setCategoryId(null);
        setCategoryName('');
        setParentCategory(null);
        return;
    }

    try {
      const response = await fetch(`https://ayushvatika.com/api/categories/${id}`);
      // const response = await fetch(`http://localhost:8484/api/categories/${id}`);
      if (response.ok) {
        const data: Category = await response.json();
        setCategoryId(data.id);
        setCategoryName(data.name);
        setParentCategory(data.parentId || null);
      } else {
        setMessage(`Failed to load category for editing: ${response.statusText}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to server.');
      setMessageType('error');
    }
  };

  useEffect(() => {
    fetchExistingCategories();
    fetchCategoryForEdit();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!id) {
        setMessage('Please select a category from the "Existing Categories" list on the right to edit it.');
        setMessageType('error');
        return;
    }
    if (!categoryName.trim()) {
      setMessage('Category Name cannot be empty.');
      setMessageType('error');
      return;
    }
    if (parentCategory === Number(id)) {
        setMessage('A category cannot be its own parent.');
        setMessageType('error');
        return;
    }

    try {
      const payload = {
        name: categoryName.trim(),
        parentId: parentCategory,
        icon: null 
      };

      const response = await fetch(`https://ayushvatika.com/api/categories/${id}`, {
      // const response = await fetch(`http://localhost:8484/api/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage('Category updated successfully!');
        setMessageType('success');
        fetchExistingCategories();
        setTimeout(() => navigate('/admin/category/list'), 2000);
      } else {
        const errorData = await response.json();
        setMessage(`Failed to update category: ${errorData.message || response.statusText}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to server.');
      setMessageType('error');
    }
  };

  const handleCancel = () => {
    navigate('/admin/category/list');
  };

  const renderDropdownOptions = (categories: Category[], level: number = 0) => {
    let options: React.ReactNode[] = [];
    categories.forEach(cat => {
        if (cat.id !== Number(id)) {
            options.push(
                <option key={cat.id} value={cat.id}>
                {"\u00A0\u00A0".repeat(level)} {cat.name}
                </option>
            );
            if (cat.subCategories && cat.subCategories.length > 0) {
                options = options.concat(renderDropdownOptions(cat.subCategories, level + 1));
            }
        }
    });
    return options;
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) newSet.delete(categoryId);
      else newSet.add(categoryId);
      return newSet;
    });
  };

  const getFaIcon = (iconName: string, size: number = 16) => {
    const IconComponent = (FaIcons as any)[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const renderCategoryTree = (categories: Category[], level: number = 0) => {
    return (
      <ul className={`ml-${level * 4}`}>
        {categories.map(cat => {
           const isSelected = cat.id === Number(id);
           return (
            <li key={cat.id} className="mt-2">
                <div className={`flex items-center transition-colors ${isSelected ? 'text-green-500 font-bold' : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}>
                {cat.subCategories && cat.subCategories.length > 0 && (
                    <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleCategoryExpansion(cat.id);
                    }}
                    className="mr-2 focus:outline-none"
                    >
                    {expandedCategories.has(cat.id) ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                    </button>
                )}
                {(!cat.subCategories || cat.subCategories.length === 0) && (
                    <span className="mr-4 w-4"></span>
                )}
                
                <div 
                    className="flex items-center cursor-pointer p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                    onClick={() => navigate(`/admin/category/edit/${cat.id}`)}
                    title="Click to Edit"
                >
                    {getFaIcon(cat.icon || 'FaFolder', 18)}
                    <span className="ml-2 text-lg">{cat.name}</span>
                </div>
                </div>
                {expandedCategories.has(cat.id) && cat.subCategories && cat.subCategories.length > 0 && (
                renderCategoryTree(cat.subCategories, level + 1)
                )}
            </li>
        )})}
      </ul>
    );
  };
    
  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center transition-colors">Edit Product Category</h1>
        {message && (
          <div className={`p-4 mb-6 rounded-lg text-white text-center font-semibold ${messageType === 'success' ? 'bg-green-600' : 'bg-red-600'} shadow-md`}>
            {message}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Side: Edit Form */}
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <FaPlusCircle className="mr-3 text-green-500" /> 
              {id ? `Edit Category ID: ${id}` : 'Select a Category to Edit'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="categoryName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  id="categoryName"
                  placeholder={id ? "e.g., Organic Vegetables" : "Please select a category from the right list..."}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 text-base shadow-sm ${!id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  disabled={!id}
                  required
                />
              </div>
              <div className="mb-7">
                <label htmlFor="parentCategory" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Parent Category (Optional)</label>
                <select
                  id="parentCategory"
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-base shadow-sm ${!id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  value={parentCategory || ''}
                  onChange={(e) => setParentCategory(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!id}
                >
                  <option value="">No Parent (Top-level Category)</option>
                  {renderDropdownOptions(existingCategories)}
                </select>
              </div>
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center font-semibold shadow-sm text-base"
                >
                  <FaTimesCircle className="mr-2" /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={!id}
                  className={`px-6 py-3 rounded-lg text-white transition-colors flex items-center justify-center font-semibold shadow-md text-base ${!id ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  <FaSave className="mr-2" /> Update Category
                </button>
              </div>
            </form>
          </div>

          {/* Right Side: Existing Categories List */}
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <FaFolderOpen className="mr-3 text-blue-500" /> Select Category to Edit
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">Click on any category name below to load it into the form.</p>
            
            {existingCategories.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No categories found.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto custom-scrollbar pr-4">
                {renderCategoryTree(existingCategories)}
              </div>
            )}
          </div>

        </div>
      </div>
    </AdminLayout>
  );
};

export default EditCategoryPage;